import React, { useState, useEffect } from 'react';
import { useLanguage, useAuth, api } from '../context/AppContext';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Bell, BellOff, Loader2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

// Convert base64 URL to Uint8Array for subscription
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const PushNotificationToggle = ({ compact = false }) => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [loading, setLoading] = useState(true);
  const [permission, setPermission] = useState('default');

  useEffect(() => {
    checkSupport();
  }, []);

  useEffect(() => {
    if (user && isSupported) {
      checkSubscription();
    }
  }, [user, isSupported]);

  const checkSupport = () => {
    const supported = 'serviceWorker' in navigator && 
                      'PushManager' in window && 
                      'Notification' in window &&
                      VAPID_PUBLIC_KEY;
    setIsSupported(supported);
    
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }
    
    if (!supported) {
      setLoading(false);
    }
  };

  const checkSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setIsSubscribed(!!subscription);
    } catch (error) {
      console.error('Error checking subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const subscribe = async () => {
    setLoading(true);
    try {
      // Request permission first
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);
      
      if (permissionResult !== 'granted') {
        toast.error(
          language === 'fr' 
            ? 'Permission refusée. Activez les notifications dans les paramètres de votre navigateur.'
            : 'Permission denied. Enable notifications in your browser settings.'
        );
        setLoading(false);
        return;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;
      
      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });

      // Send subscription to backend
      const subscriptionData = subscription.toJSON();
      await api.post('/notifications/subscribe', {
        endpoint: subscriptionData.endpoint,
        keys: subscriptionData.keys
      });

      setIsSubscribed(true);
      toast.success(
        language === 'fr' 
          ? 'Notifications activées! Tu recevras des rappels pour tes repas.'
          : 'Notifications enabled! You will receive meal reminders.'
      );
    } catch (error) {
      console.error('Error subscribing to push:', error);
      toast.error(
        language === 'fr' 
          ? 'Erreur lors de l\'activation des notifications'
          : 'Error enabling notifications'
      );
    } finally {
      setLoading(false);
    }
  };

  const unsubscribe = async () => {
    setLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        // Unsubscribe from push manager
        await subscription.unsubscribe();
        
        // Remove from backend
        await api.delete(`/notifications/unsubscribe?endpoint=${encodeURIComponent(subscription.endpoint)}`);
      }

      setIsSubscribed(false);
      toast.success(
        language === 'fr' 
          ? 'Notifications désactivées'
          : 'Notifications disabled'
      );
    } catch (error) {
      console.error('Error unsubscribing:', error);
      toast.error(
        language === 'fr' 
          ? 'Erreur lors de la désactivation'
          : 'Error disabling notifications'
      );
    } finally {
      setLoading(false);
    }
  };

  if (!isSupported) {
    return null;
  }

  // Compact version for settings page
  if (compact) {
    return (
      <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-lg">
        <div className="flex items-center gap-3">
          {isSubscribed ? (
            <Bell className="w-5 h-5 text-primary" />
          ) : (
            <BellOff className="w-5 h-5 text-muted-foreground" />
          )}
          <div>
            <p className="text-sm font-medium text-foreground">
              {language === 'fr' ? 'Notifications push' : 'Push notifications'}
            </p>
            <p className="text-xs text-muted-foreground">
              {isSubscribed 
                ? (language === 'fr' ? 'Rappels de repas activés' : 'Meal reminders enabled')
                : (language === 'fr' ? 'Recevoir des rappels de repas' : 'Receive meal reminders')
              }
            </p>
          </div>
        </div>
        <Button
          onClick={isSubscribed ? unsubscribe : subscribe}
          disabled={loading}
          variant={isSubscribed ? 'outline' : 'default'}
          size="sm"
          className={isSubscribed ? 'border-primary text-primary' : 'bg-primary text-primary-foreground'}
          data-testid="push-notification-toggle"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : isSubscribed ? (
            <>{language === 'fr' ? 'Désactiver' : 'Disable'}</>
          ) : (
            <>{language === 'fr' ? 'Activer' : 'Enable'}</>
          )}
        </Button>
      </div>
    );
  }

  // Full card version for dashboard/onboarding
  return (
    <AnimatePresence>
      {!isSubscribed && permission !== 'denied' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
        >
          <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/30" data-testid="push-notification-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-primary/20 rounded-full">
                  <Bell className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-sm uppercase tracking-tight text-foreground mb-1">
                    {language === 'fr' ? 'Active les notifications' : 'Enable notifications'}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {language === 'fr' 
                      ? 'Reçois des rappels pour ne jamais oublier de logger tes repas!'
                      : 'Get reminders so you never forget to log your meals!'
                    }
                  </p>
                </div>
                <Button
                  onClick={subscribe}
                  disabled={loading}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="enable-notifications-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Bell className="w-4 h-4 mr-2" />
                      {language === 'fr' ? 'Activer' : 'Enable'}
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PushNotificationToggle;
